import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaFlag, FaEye, FaBan, FaCheck, FaTimes, FaFilter, FaChevronDown } from 'react-icons/fa';

interface Report {
  id: string;
  user_id: string | null;
  content_id: string;
  content_preview: string;
  reason: string;
  details: string | null;
  status: 'open' | 'under_review' | 'closed' | 'blocked';
  created_at: string;
  user_email?: string;
}

interface ModerationAction {
  id: string;
  report_id: string;
  actor_id: string | null;
  action: string;
  note: string | null;
  created_at: string;
}

export default function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedReason, setSelectedReason] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [processingReports, setProcessingReports] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const user = getCurrentUser();

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'open', label: 'Ouvert' },
    { value: 'under_review', label: 'En cours de révision' },
    { value: 'closed', label: 'Fermé' },
    { value: 'blocked', label: 'Bloqué' }
  ];

  const reasonOptions = [
    { value: 'all', label: 'Toutes les raisons' },
    { value: 'violence', label: 'Violence' },
    { value: 'hate', label: 'Haine/Discrimination' },
    { value: 'sexual', label: 'Sexuel' },
    { value: 'harassment', label: 'Harcèlement' },
    { value: 'privacy', label: 'Atteinte à la vie privée' },
    { value: 'copyright', label: 'Droits d\'auteur' },
    { value: 'self_harm', label: 'Automutilation' },
    { value: 'other', label: 'Autre' }
  ];

  useEffect(() => {
    // Check if user is admin
    if (!user?.is_admin) {
      toast.error('Accès non autorisé');
      navigate('/dashboard');
      return;
    }

    loadReports();
  }, [selectedStatus, selectedReason]);

  const loadReports = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('report_ai_content_reports')
        .select(`
          *,
          user_email:stripeusers!report_ai_content_reports_user_id_fkey(email)
        `)
        .order('created_at', { ascending: false });

      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }

      if (selectedReason !== 'all') {
        query = query.eq('reason', selectedReason);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Format the data to extract email from nested object
      const formattedReports = (data || []).map(report => ({
        ...report,
        user_email: report.user_email?.email || 'Utilisateur supprimé'
      }));

      setReports(formattedReports);
    } catch (error: any) {
      console.error('Error loading reports:', error);
      toast.error('Erreur lors du chargement des signalements');
    } finally {
      setLoading(false);
    }
  };

  const updateReportStatus = async (reportId: string, newStatus: string, note?: string) => {
    try {
      setProcessingReports(prev => new Set(prev).add(reportId));

      // Update report status
      const { error: updateError } = await supabase
        .from('report_ai_content_reports')
        .update({ status: newStatus })
        .eq('id', reportId);

      if (updateError) throw updateError;

      // Create moderation action
      const { error: actionError } = await supabase
        .from('report_moderation_actions')
        .insert({
          report_id: reportId,
          actor_id: user?.id,
          action: newStatus,
          note: note || null
        });

      if (actionError) throw actionError;

      // Update local state
      setReports(prev => prev.map(report => 
        report.id === reportId ? { ...report, status: newStatus as any } : report
      ));

      toast.success(`Statut mis à jour : ${statusOptions.find(s => s.value === newStatus)?.label}`);
    } catch (error: any) {
      console.error('Error updating report status:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setProcessingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      open: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Ouvert' },
      under_review: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'En révision' },
      closed: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Fermé' },
      blocked: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Bloqué' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getReasonBadge = (reason: string) => {
    const reasonConfig = {
      violence: { bg: 'bg-red-500/20', text: 'text-red-400' },
      hate: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
      sexual: { bg: 'bg-pink-500/20', text: 'text-pink-400' },
      harassment: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
      privacy: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
      copyright: { bg: 'bg-green-500/20', text: 'text-green-400' },
      self_harm: { bg: 'bg-red-600/20', text: 'text-red-300' },
      other: { bg: 'bg-gray-500/20', text: 'text-gray-400' }
    };

    const config = reasonConfig[reason as keyof typeof reasonConfig] || reasonConfig.other;
    const label = reasonLabels[reason as keyof typeof reasonLabels] || reason;

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Signalements de Contenu</h1>
            <p className="mt-2 text-gray-400">
              Gérer les signalements de contenu IA
            </p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white transition-colors duration-200"
          >
            <FaFilter className="w-4 h-4" />
            <span>Filtres</span>
            <FaChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8 border border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Statut
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Raison
                </label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {reasonOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Reports List */}
        <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Chargement des signalements...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="p-8 text-center">
              <FaFlag className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Aucun signalement</h3>
              <p className="text-gray-400">Aucun signalement trouvé avec les filtres actuels.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {reports.map((report) => (
                <div key={report.id} className="p-6 hover:bg-gray-700/30 transition-colors duration-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                          <FaFlag className="w-4 h-4 text-red-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            Signalé par : {report.user_email}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(report.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 mb-3">
                        {getStatusBadge(report.status)}
                        {getReasonBadge(report.reason)}
                      </div>
                    </div>
                  </div>

                  {/* Content Preview */}
                  <div className="bg-gray-900/50 rounded-lg p-4 mb-4 border border-gray-600/30">
                    <p className="text-sm text-gray-400 mb-2">Contenu signalé :</p>
                    <p className="text-sm text-white/80 leading-relaxed">
                      {report.content_preview}
                    </p>
                  </div>

                  {/* Details */}
                  {report.details && (
                    <div className="bg-gray-900/30 rounded-lg p-3 mb-4 border border-gray-600/20">
                      <p className="text-sm text-gray-400 mb-1">Détails :</p>
                      <p className="text-sm text-white/70">{report.details}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {report.status === 'open' && (
                      <button
                        onClick={() => updateReportStatus(report.id, 'under_review')}
                        disabled={processingReports.has(report.id)}
                        className="flex items-center space-x-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition-colors duration-200 disabled:opacity-50"
                      >
                        <FaEye className="w-4 h-4" />
                        <span className="text-sm">Examiner</span>
                      </button>
                    )}
                    
                    {report.status !== 'blocked' && (
                      <button
                        onClick={() => updateReportStatus(report.id, 'blocked', 'Contenu bloqué par modération')}
                        disabled={processingReports.has(report.id)}
                        className="flex items-center space-x-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors duration-200 disabled:opacity-50"
                      >
                        <FaBan className="w-4 h-4" />
                        <span className="text-sm">Bloquer</span>
                      </button>
                    )}
                    
                    {report.status !== 'closed' && (
                      <button
                        onClick={() => updateReportStatus(report.id, 'closed', 'Signalement résolu')}
                        disabled={processingReports.has(report.id)}
                        className="flex items-center space-x-2 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition-colors duration-200 disabled:opacity-50"
                      >
                        <FaCheck className="w-4 h-4" />
                        <span className="text-sm">Fermer</span>
                      </button>
                    )}

                    {processingReports.has(report.id) && (
                      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-500/20 rounded-lg">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        <span className="text-sm text-gray-400">Traitement...</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          {statusOptions.slice(1).map(status => {
            const count = reports.filter(r => r.status === status.value).length;
            return (
              <div key={status.value} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{count}</div>
                  <div className="text-sm text-gray-400">{status.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}